'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Calculator } from 'lucide-react';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Skeleton } from '@/shared/ui/shadcn/skeleton';
import { ProjectSubpageLayout } from '@/shared/ui';
import { useProjectDesignLines } from '../hooks/use-design-line';
import { EstimateCard } from './EstimateCard';

interface ProjectEstimatesPageProps {
  projectId: string;
}

/**
 * Dedicated project Estimates page (FE3) — mirrors `ProjectAlbumsPage`. Lists
 * one card per running estimate (design line), the same list as the building
 * page carousel. Each card opens the Estimate tab of the design it prices.
 */
export function ProjectEstimatesPage({ projectId }: ProjectEstimatesPageProps) {
  const t = useTranslations('common.estimate');
  const tProject = useTranslations('project');

  // One card per running estimate (design line), the same list as the
  // building page carousel: never one card per version.
  const { data: lines = [], isLoading } = useProjectDesignLines(projectId);
  const estimates = lines.flatMap((line) =>
    line.currentEstimate
      ? [{ estimate: line.currentEstimate, title: line.display_title }]
      : []
  );

  return (
    <ProjectSubpageLayout
      projectId={projectId}
      backLabel={tProject('backToProject')}
      title={t('page.title')}
    >
      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="aspect-[4/3] w-full rounded-xl" />
          ))}
        </div>
      ) : estimates.length === 0 ? (
        <EmptyState
          icon={<Calculator />}
          title={t('page.empty.title')}
          description={t.rich('page.empty.description', {
            link: (chunks) => (
              <Link
                href={`/project/${projectId}/concepts`}
                className="text-primary underline-offset-4 hover:underline"
              >
                {chunks}
              </Link>
            ),
          })}
          centered
        />
      ) : (
        // One tile per row on phones, 2 / 3 / 4 as the viewport widens
        // (Android/Apple parity: adaptive grid with a ~270dp minimum tile).
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {estimates.map(({ estimate, title }) => (
            <EstimateCard
              key={estimate.id}
              estimate={estimate}
              title={title}
              projectId={projectId}
              variant="list"
            />
          ))}
        </div>
      )}
    </ProjectSubpageLayout>
  );
}
